<template>
    <PanelPage>
        <template v-slot:title>
            <SmartBreadcrumbs
                :pages="[
                    {
                        name: $tc('messages.activities', 2),
                        href: meta.links.index,
                    },
                    {
                        name: activity.title,
                        href: activity.links.show,
                    },
                    {
                        name: $t('messages.assign_evaluation_value'),
                        href: null,
                    },
                ]"
            />
        </template>

        <span>
            {{ $tc('messages.evaluation_criteria', 2) }}
        </span>

        <div class="flex justify-end space-x-2 items-center">
            <EvaluationCriteriaCreateForm
                :options="options"
                :url="meta.links.evaluation_criteria_store"
                :defaults="activity.defaults"
            />
        </div>

        <div>
            <label
                v-for="category in options.categories"
                :key="category.id"
                :class="selected.includes(category.id) ? 'bg-indigo-100 text-indigo-800' : 'bg-gray-100 text-gray-600'"
                class="cursor-pointer mr-3 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
            >
                <input :id="category.id" :value="category.id" type="checkbox" v-model="selected" class="hidden" />
                <span>
                    {{ category.name }}
                </span>
            </label>
        </div>

        <div class="flex flex-col mt-5">
            <div class="-my-2 py-2 overflow-x-auto sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8">
                <div
                    class="align-middle inline-block min-w-full shadow overflow-hidden sm:rounded-lg border-b border-gray-200"
                >
                    <table class="min-w-full divide-y divide-gray-200">
                        <thead>
                            <tr>
                                <th
                                    class="max-w-xs px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                >
                                    -
                                </th>
                                <th
                                    class="max-w-xs px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                >
                                    {{ $t('messages.title') }}
                                </th>
                                <th
                                    class="max-w-xs px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                >
                                    {{ $t('messages.description') }}
                                </th>
                                <th
                                    class="max-w-xs px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                >
                                    {{ $tc('messages.grade_level', 2) }}
                                </th>
                                <th
                                    class="max-w-xs px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                >
                                    {{ $tc('messages.dimensions', 2) }}
                                </th>
                                <th
                                    class="max-w-xs px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                >
                                    <span class="sr-only">{{ $t('messages.actions') }}</span>
                                </th>
                            </tr>
                        </thead>
                        <TransitionGroup
                            enter-active-class="transition-opacity ease-linear duration-300"
                            enter-from-class="opacity-0"
                            enter-to-class="opacity-100"
                            leave-active-class="transition-opacity ease-linear duration-300"
                            leave-from-class="opacity-100"
                            leave-to-class="opacity-0"
                            tag="tbody"
                        >
                            <tr
                                :class="index % 2 === 0 ? 'bg-white' : 'bg-gray-50'"
                                v-for="(evaluationCriterion, index) in filteredEvaluationCriteria"
                                :key="evaluationCriterion.id"
                            >
                                <td class="px-6 py-4 whitespace-nowrap text-sm leading-5 text-gray-500">
                                    <label class="sr-only">{{ evaluationCriterion.title }}</label>
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        class="h-6 w-6 text-indigo-400"
                                        viewBox="0 0 24 24"
                                        stroke-width="2"
                                        stroke="currentColor"
                                        fill="none"
                                        stroke-linecap="round"
                                        stroke-linejoin="round"
                                        v-if="evaluationCriterion.is_selected"
                                    >
                                        <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                                        <circle cx="12" cy="12" r="9"></circle>
                                        <path d="M9 12l2 2l4 -4"></path>
                                    </svg>
                                    <InertiaLink
                                        v-else
                                        method="patch"
                                        as="button"
                                        :href="meta.links.update"
                                        :data="{ evaluation_criterion_id: evaluationCriterion.id }"
                                        :title="$t('messages.assign_evaluation_value')"
                                        class="text-indigo-700 bg-indigo-100 hover:bg-indigo-200 inline-flex items-center px-1.5 py-1.5 border border-transparent text-xs font-medium rounded focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                                        ><svg viewBox="0 0 20 20" fill="currentColor" class="pencil w-4 h-4">
                                            <path
                                                fill-rule="evenodd"
                                                d="M6.672 1.911a1 1 0 10-1.932.518l.259.966a1 1 0 001.932-.518l-.26-.966zM2.429 4.74a1 1 0 10-.517 1.932l.966.259a1 1 0 00.517-1.932l-.966-.26zm8.814-.569a1 1 0 00-1.415-1.414l-.707.707a1 1 0 101.415 1.415l.707-.708zm-7.071 7.072l.707-.707A1 1 0 003.465 9.12l-.708.707a1 1 0 001.415 1.415zm3.2-5.171a1 1 0 00-1.3 1.3l4 10a1 1 0 001.823.075l1.38-2.759 3.018 3.02a1 1 0 001.414-1.415l-3.019-3.02 2.76-1.379a1 1 0 00-.076-1.822l-10-4z"
                                                clip-rule="evenodd"
                                            ></path>
                                        </svg>
                                    </InertiaLink>
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm leading-5 text-gray-500">
                                    {{ evaluationCriterion.title }}
                                </td>
                                <td class="px-6 py-4 text-sm leading-5 text-gray-500">
                                    {{ evaluationCriterion.description }}
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm leading-5 text-gray-500">
                                    {{ evaluationCriterion.grade_level_names }}
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm leading-5 text-gray-500">
                                    <div
                                        v-for="dimension in evaluationCriterion.dimension_abbreviations"
                                        :key="dimension.id"
                                        class="inline-block mr-1"
                                    >
                                        <span :title="dimension.title">{{ dimension.abbreviation }}</span>
                                    </div>
                                    <div class="flex flex-wrap space-x-1">
                                        <BadgePill
                                            v-for="category in evaluationCriterion.categories"
                                            :key="category.id"
                                        >
                                            {{ category.name }}
                                        </BadgePill>
                                    </div>
                                </td>
                                <td
                                    class="px-6 py-4 whitespace-nowrap text-sm leading-5 text-gray-500 flex space-x-2 justify-end"
                                >
                                    <InertiaSquareLinkButton action="edit" :href="evaluationCriterion.links.edit" />
                                </td>
                            </tr>
                        </TransitionGroup>
                    </table>
                </div>
            </div>
        </div>
    </PanelPage>
</template>

<script>
import Layout from '@/Shared/Layout.vue';
import PanelPage from '@/Shared/PanelPage.vue';
import InertiaSquareLinkButton from '@/components/InertiaSquareLinkButton.vue';
import EvaluationCriteriaCreateForm from '@/components/pageSpecific/EvaluationCriteriaCreateForm.vue';
import BadgePill from '@/components/BadgePill.vue';
import { TransitionGroup } from 'vue';
import { Link as InertiaLink } from '@inertiajs/vue3';

export default {
    components: {
        InertiaSquareLinkButton,
        PanelPage,
        EvaluationCriteriaCreateForm,
        BadgePill,
        TransitionGroup,
        InertiaLink,
    },
    props: ['activity', 'meta', 'options', 'evaluationCriteria'],
    name: 'Edit',
    layout: Layout,
    data() {
        return {
            selected: this.activity.category_ids,
        };
    },
    computed: {
        filteredEvaluationCriteria() {
            return this.evaluationCriteria.filter((e) => {
                return this.selected.some((c) => e.category_ids.includes(c));
            });
        },
    },
};
</script>
